import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import DriverPartnerOnboard from "@/components/mobile/DriverPartnerOnboard";
import {
  isMissingDeliveryPartnerAccountError,
  loadSavedPartnerPhone,
  loginDeliveryPartnerFirebase,
  loginOrNeedsPartnerSignup,
} from "@/lib/driverPartner";
import { isFirebaseConfigured } from "@/lib/firebaseConfig";
import {
  confirmFirebaseOtp,
  firebasePhoneErrorMessage,
  resetFirebaseRecaptcha,
  sendFirebaseOtp,
} from "@/lib/firebasePhoneAuth";

const PINK = "#EE2677";
const RESEND_SECONDS = 60;

/** Partner login / sign-up — Firebase SMS OTP then hub or onboard form. */
export default function DriverPartnerAuthGate({ onComplete, initialMode = "login" }) {
  const [mode, setMode] = useState(initialMode);
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState(() => loadSavedPartnerPhone());
  const [otp, setOtp] = useState("");
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [verifiedFirebaseToken, setVerifiedFirebaseToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [resendIn, setResendIn] = useState(0);

  const cleanPhone = phone.replace(/\D/g, "").slice(-10);
  const maskedPhone =
    cleanPhone.length === 10 ? `+91 ${cleanPhone.slice(0, 5)} ${cleanPhone.slice(5)}` : "";

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  useEffect(() => () => resetFirebaseRecaptcha(), []);

  const resetPhoneStep = () => {
    resetFirebaseRecaptcha();
    setConfirmationResult(null);
    setVerifiedFirebaseToken("");
    setStep("phone");
    setOtp("");
    setErr("");
    setInfo("");
    setResendIn(0);
  };

  const sendCode = async (e) => {
    e?.preventDefault?.();
    if (cleanPhone.length !== 10) {
      setErr("Enter your 10-digit mobile number");
      return;
    }
    if (!isFirebaseConfigured()) {
      setErr("SMS login is not configured yet. Ask your manager.");
      return;
    }
    setBusy(true);
    setErr("");
    setInfo("");
    try {
      const result = await sendFirebaseOtp(cleanPhone);
      setConfirmationResult(result);
      setStep("otp");
      setOtp("");
      setResendIn(RESEND_SECONDS);
      setInfo("Check your phone for a 6-digit code via SMS.");
    } catch (ex) {
      setErr(firebasePhoneErrorMessage(ex));
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (e) => {
    e?.preventDefault?.();
    const code = otp.replace(/\D/g, "");
    if (code.length !== 6) {
      setErr("Enter all 6 digits from SMS");
      return;
    }
    if (!confirmationResult) {
      setErr("Tap Send code and try again.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const firebaseIdToken = await confirmFirebaseOtp(confirmationResult, code);
      setVerifiedFirebaseToken(firebaseIdToken);

      if (mode === "signup") {
        // Existing partners who land on /dd/join (or tap Sign up) must not see
        // an empty "create profile" form — log them straight in when possible.
        const result = await loginOrNeedsPartnerSignup(firebaseIdToken);
        if (result.status === "logged_in") {
          onComplete?.(result.data);
          return;
        }
        setStep("signup");
        setInfo("");
        return;
      }

      const data = await loginDeliveryPartnerFirebase(firebaseIdToken);
      onComplete?.(data);
    } catch (ex) {
      const msg = ex.message || firebasePhoneErrorMessage(ex);
      if (mode === "login" && isMissingDeliveryPartnerAccountError(msg)) {
        setErr("No account on this number. Tap New partner sign-up below.");
      } else {
        setErr(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const cancelSignupForm = async () => {
    // Back must not feel like logout: if this number already has a profile,
    // finish login with the OTP we already verified.
    if (verifiedFirebaseToken) {
      setBusy(true);
      setErr("");
      try {
        const result = await loginOrNeedsPartnerSignup(verifiedFirebaseToken);
        if (result.status === "logged_in") {
          onComplete?.(result.data);
          return;
        }
      } catch (ex) {
        setErr(ex.message || firebasePhoneErrorMessage(ex));
      } finally {
        setBusy(false);
      }
    }
    setMode("login");
    resetPhoneStep();
  };

  if (mode === "signup" && step === "signup" && verifiedFirebaseToken) {
    return (
      <DriverPartnerOnboard
        mandatory
        initialPhone={cleanPhone}
        phoneLocked
        firebaseIdToken={verifiedFirebaseToken}
        onComplete={onComplete}
        onCancel={cancelSignupForm}
      />
    );
  }

  const title = mode === "signup" ? "Partner sign-up" : "Welcome back";
  const sub =
    step === "otp"
      ? `Enter the 6-digit code we sent to ${maskedPhone} via SMS.`
      : mode === "signup"
        ? "Verify your mobile with SMS — then complete your partner profile."
        : "Verify your mobile with a one-time SMS code to open the delivery board.";

  return (
    <div className="da-partner-auth">
      <style>{`
        .da-partner-auth {
          min-height: 100dvh;
          background: #F5F2EA;
          color: #2D2D2D;
          padding: 12px 14px 28px;
          max-width: 480px;
          margin: 0 auto;
          font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
        }
        .da-partner-auth * { box-sizing: border-box; }
        .da-partner-brand { text-align: center; margin: 24px 0 28px; }
        .da-partner-brand-kicker {
          font-size: 11px; font-weight: 800; letter-spacing: 0.12em;
          text-transform: uppercase; color: rgba(45,45,45,0.45); margin: 0 0 4px;
        }
        .da-partner-brand-name { font-size: 28px; font-weight: 900; color: ${PINK}; margin: 0; }
        .da-partner-card {
          background: #fff; border-radius: 20px; padding: 20px 18px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06);
        }
        .da-partner-title { font-size: 20px; font-weight: 800; margin: 0 0 6px; }
        .da-partner-sub { font-size: 14px; color: rgba(45,45,45,0.65); margin: 0 0 16px; line-height: 1.45; }
        .da-partner-info {
          margin-bottom: 12px; background: #FFF0F6; border: 1px solid #F9A8D4;
          border-radius: 12px; padding: 10px 12px; font-size: 13px; font-weight: 600;
          color: #9D174D; text-align: center;
        }
        .da-partner-field label { display: block; font-size: 13px; font-weight: 700; margin-bottom: 6px; }
        .da-partner-field input {
          width: 100%; border: 2px solid #E5E7EB; border-radius: 14px;
          padding: 14px; font-size: 18px; background: #fff;
        }
        .da-partner-otp {
          letter-spacing: 0.35em; text-align: center; font-weight: 800;
        }
        .da-partner-primary {
          width: 100%; border: none; border-radius: 16px; padding: 16px;
          font-size: 17px; font-weight: 800; color: #fff; background: ${PINK};
          cursor: pointer; margin-top: 14px;
        }
        .da-partner-primary:disabled { opacity: 0.6; cursor: wait; }
        .da-partner-link {
          width: 100%; margin-top: 12px; border: none; background: transparent;
          color: ${PINK}; font-size: 15px; font-weight: 800; cursor: pointer; padding: 10px;
        }
        .da-partner-err { color: #B91C1C; font-size: 14px; font-weight: 600; margin-top: 10px; }
        .da-partner-resend {
          width: 100%; margin-top: 10px; border: none; background: transparent;
          color: ${PINK}; font-size: 14px; font-weight: 800; cursor: pointer; padding: 8px;
        }
        .da-partner-resend:disabled { color: rgba(45,45,45,0.4); cursor: default; }
      `}</style>

      <div className="da-partner-brand">
        <p className="da-partner-brand-kicker">Delivery app</p>
        <h1 className="da-partner-brand-name">The Table Top</h1>
      </div>

      <div className="da-partner-card">
        <h2 className="da-partner-title">{title}</h2>
        <p className="da-partner-sub">{sub}</p>

        {info && !err ? <p className="da-partner-info">{info}</p> : null}

        {step === "phone" ? (
          <form onSubmit={sendCode}>
            <div className="da-partner-field">
              <label htmlFor="dp-login-phone">Mobile number</label>
              <input
                id="dp-login-phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="10-digit mobile"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
            </div>
            {err ? <p className="da-partner-err">{err}</p> : null}
            <button type="submit" className="da-partner-primary" disabled={busy}>
              {busy ? <Loader2 className="animate-spin" size={18} style={{ verticalAlign: "middle" }} /> : null}
              {busy ? " Sending code…" : "Send login code"}
            </button>
          </form>
        ) : (
          <form onSubmit={verifyCode}>
            <div className="da-partner-field">
              <label htmlFor="dp-login-otp">6-digit SMS code</label>
              <input
                id="dp-login-otp"
                type="tel"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="••••••"
                className="da-partner-otp"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                required
              />
            </div>
            {err ? <p className="da-partner-err">{err}</p> : null}
            <button
              type="submit"
              className="da-partner-primary"
              disabled={busy || otp.replace(/\D/g, "").length !== 6}
            >
              {busy ? " Verifying…" : mode === "signup" ? "Verify & continue" : "Verify & sign in"}
            </button>
            <button
              type="button"
              className="da-partner-resend"
              disabled={busy || resendIn > 0}
              onClick={sendCode}
            >
              {resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code"}
            </button>
            <button type="button" className="da-partner-link" onClick={resetPhoneStep}>
              Change phone number
            </button>
          </form>
        )}

        {mode === "login" && step === "phone" ? (
          <button
            type="button"
            className="da-partner-link"
            onClick={() => {
              setMode("signup");
              resetPhoneStep();
            }}
          >
            New partner? Sign up here
          </button>
        ) : null}

        {mode === "signup" && step === "phone" ? (
          <button
            type="button"
            className="da-partner-link"
            onClick={() => {
              setMode("login");
              resetPhoneStep();
            }}
          >
            Already registered? Log in
          </button>
        ) : null}
      </div>
    </div>
  );
}
